import React, { useState, useEffect, useRef } from "react";
import {
  Container,
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  Alert,
  CircularProgress,
} from "@mui/material";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import API_BASE_URL from "../Config/config";
import WorkspaceSelector from "../components/workspaces/WorkspaceSelector";

function CodingTest() {
  const { applicationId } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [codingTest, setCodingTest] = useState(null);
  const [code, setCode] = useState("");
  const [error, setError] = useState("");
  const isSubmittingRef = useRef(false);

  // =========================
  // FETCH CODING TEST
  // =========================
  useEffect(() => {
    if (applicationId && localStorage.getItem(`terminated_${applicationId}`)) {
      localStorage.removeItem(`terminated_${applicationId}`);
      setError("terminated");
      // Notify backend if refresh was detected
      const url = `${API_BASE_URL}/interviewRoutes/ai-interview/terminate/${applicationId}`;
      const payload = JSON.stringify({ applicationId, interviewType: 'CODING_TEST', isTerminated: true, answers: [] });
      if (navigator.sendBeacon) navigator.sendBeacon(url, payload);
    }

    async function fetchTest() {
      try {
        const res = await axios.get(
            `/api/coding-test/${applicationId}`
        );

        setCodingTest(res.data);

        if (res.data?.candidate_code) {
          setCode(res.data.candidate_code);
        }
      } catch (err) {
        console.error("Failed to load coding test:", err);
        if (err.response?.status === 400 && err.response?.data?.message === "Interview already terminated") {
          setError("terminated");
        } else {
          setError("Failed to load coding test.");
        }
      } finally {
        setLoading(false);
      }
    }

    fetchTest();
  }, [applicationId]);

  useEffect(() => {
    const handleUnload = () => {
      if (isSubmittingRef.current || loading) return;

      // 🔥 Force termination on refresh/close
      localStorage.setItem(`terminated_${applicationId}`, 'true');

      const url = `${API_BASE_URL}/interviewRoutes/ai-interview/terminate/${applicationId}`;
      const payload = JSON.stringify({
        applicationId,
        interviewType: 'CODING_TEST',
        isTerminated: true,
        answers: [{
          question: codingTest?.question_text || "Coding Test",
          answer: code || "",
        }],
      });

      if (navigator.sendBeacon) {
        navigator.sendBeacon(url, payload);
      } else {
        fetch(url, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: payload,
          keepalive: true,
        });
      }
    };

    window.addEventListener("beforeunload", handleUnload);
    window.addEventListener("unload", handleUnload);

    return () => {
      window.removeEventListener("beforeunload", handleUnload);
      window.removeEventListener("unload", handleUnload);
    };
  }, [applicationId, codingTest, code, loading]);

  // =========================
  // SUBMIT TEST
  // =========================
  async function handleSubmit() {
    if (isSubmittingRef.current) return;
    isSubmittingRef.current = true;
    try {
      setSubmitting(true);

      await axios.post(
          `/api/coding-test/${applicationId}/submit`,
          {
            code,
          }
      );

      alert("✅ Coding test submitted successfully!");

      navigate("/candidate/dashboard");
    } catch (err) {
      console.error("Submit failed:", err);
      setError("Failed to submit coding test.");
    } finally {
      setSubmitting(false);
    }
  }

  if (error === "terminated") {
    return (
      <Box sx={{ textAlign: "center", mt: 10 }}>
        <Typography variant="h4" gutterBottom>Interview Already Terminated</Typography>
        <Button variant="contained" onClick={() => navigate("/candidate/dashboard")}>
          Go Back
        </Button>
      </Box>
    );
  }

  // =========================
  // LOADING
  // =========================
  if (loading) {
    return (
        <Box sx={{ display: "flex", justifyContent: "center", mt: 8 }}>
          <CircularProgress />
        </Box>
    );
  }

  if (!codingTest) {
    return (
        <Container sx={{ py: 4 }}>
          <Alert severity="error">Coding test not found.</Alert>
        </Container>
    );
  }

  return (
      <Container sx={{ py: 4, maxWidth: 'xl' }}>
        {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
        )}

        <Alert severity="info" sx={{ mb: 3 }}>
          Complete the coding challenge below. Make sure to submit before time expires.
        </Alert>

        {/* Workspace Selector - Automatically loads correct editor based on question type */}
        <Box sx={{ height: '70vh', mb: 3 }}>
          <WorkspaceSelector
            question={codingTest}
            onCodeChange={(newCode) => setCode(typeof newCode === 'string' ? newCode : JSON.stringify(newCode))}
            initialCode={code}
          />
        </Box>

        {/* SUBMIT BUTTON */}
        <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center' }}>
          <Button
              variant="contained"
              size="large"
              onClick={handleSubmit}
              disabled={submitting}
              sx={{ minWidth: 200 }}
          >
            {submitting ? "Submitting..." : "Submit Test"}
          </Button>
        </Box>
      </Container>
  );
}

export default CodingTest;
